/* eslint-disable @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/restrict-template-expressions,@typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core'

import { map } from 'rxjs/operators'
import { Observable, of } from 'rxjs'

import { environment } from 'src/environments/environment'
import { FALLBACK_XERO_CLIENT_CONTACT, FALLBACK_XERO_CONTACTS } from 'src/environments/go-api-fallbacks'

import { ApiService } from '@shared/services/api.service'
import { withFeedback } from '@app/core/interceptors/feedback-context'
import { Client } from '@shared/models/client.model'
import { User, UserInList } from '@shared/models/user.model'
import { History } from '@shared/models/history.model'
import { NoteHistory } from '@shared/models/note.model'
import { toFormData } from '@core/helpers/to-form-data'
import { PagesData } from '@shared/models/pages-data.model'
import { ExportFileTypes } from '@shared/models/export-file-types.enum'
import { PlatformSettings } from '@shared/models/platform-settings.model'
import { Driver } from '@shared/models/driver.model'
import { FilterValues } from '@shared/models/filter-values.model'
import { XeroContact, XeroClientContact, XeroGetContactsParams } from '@shared/models/billing.model'

interface IsUniqueDriverId {
  isUnique: boolean
}

@Injectable({
  providedIn: 'root',
})
export class ClientsService {
  constructor(private apiService: ApiService) {}

  getClients(
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof Client)[],
  ): Observable<PagesData<Client>> {
    const toClient = (client: Client) => new Client(client)

    return this.apiService
      .getPageable<Client>('/clients/json', pageIndex, pageSize, sort, filterValues, fields)
      .pipe(map(({ data, ...rest }) => ({ data: data.map(toClient), ...rest })))
  }

  getClientList(pageIndex = 0, pageSize = 10, sort?: string, filterValues?: FilterValues, fields?: (keyof Client)[]): Observable<Client[]> {
    return this.getClients(pageIndex, pageSize, sort, filterValues, fields).pipe(
      map((response) => response.data.map((data) => new Client(data))),
    )
  }

  addClient(data: Partial<Client>): Observable<Client> {
    return this.apiService.post('/clients', data, { context: withFeedback() }).pipe(map((resp) => new Client(resp['data'])))
  }

  addClientUser(id: number | string, user: Partial<User>): Observable<User> {
    return this.apiService.post(`/clients/${id}/users`, user, { context: withFeedback() }).pipe(map((resp) => new User(resp['data'])))
  }

  getClient(id: string | number): Observable<Client> {
    return this.apiService.get(`/clients/${id}`).pipe(map((resp) => new Client(resp['data'])))
  }

  updateClient(id: number | string, client: Client): Observable<Client> {
    return this.apiService.patch(`/clients/${id}`, client, { context: withFeedback() }).pipe(map((resp) => new Client(resp['data'])))
  }

  getClientUser(clientId: number | string, userId: number | string): Observable<User | Driver> {
    return this.apiService.get(`/clients/${clientId}/users/${userId}`).pipe(map((resp) => new User(resp['data'])))
  }

  getClientHistory(id: number | string, params: any): Observable<History[]> {
    return this.apiService.get(`/clients/${id}/history/${params}`).pipe(map((resp) => resp['data'].map((history) => new History(history))))
  }

  getClientContractHistory(id: number | string, pageIndex = 0, pageSize = 10): Observable<PagesData<History>> {
    const toHistory = (client: Client) => new History(client)

    // TODO: switch when Go supports paginated list
    return this.apiService
      .getPageable(`/clients/${id}/history/contract`, pageIndex, pageSize)
      .pipe(map(({ data, ...rest }) => ({ data: data.map(toHistory), ...rest })))
  }

  getUserHistory(id: number | string, params: any): Observable<History[]> {
    return this.apiService.get(`/users/${id}/history/${params}`).pipe(map((resp) => resp['data'].map((history) => new History(history))))
  }

  getClientNotes(id: number | string, params: 'admin' | 'client'): Observable<NoteHistory[]> {
    return this.apiService.get(`/client-notes/${id}/${params}`).pipe(map((resp) => resp['data'].map((note) => new NoteHistory(note))))
  }

  addClientNote(id: number | string, note: string, noteType: 'admin' | 'client'): Observable<any> {
    const body = {
      [`${noteType}Note`]: note,
    }

    return this.apiService.post(`/clients/${id}/notes`, body, { context: withFeedback() })
  }

  deleteClientNote(id: number | string): Observable<void> {
    return this.apiService.delete(`/notes/${id}`, { context: withFeedback() })
  }

  getClientUsers(
    id: number | string,
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof User)[],
  ): Observable<PagesData<User>> {
    const toUser = (user: User) => new User(user)

    // TODO: switch when Go supports paginated list
    return this.apiService
      .getPageable<User>(`/clients/${id}/users`, pageIndex, pageSize, sort, filterValues, fields)
      .pipe(map(({ data, ...rest }) => ({ data: data.map(toUser), ...rest })))
  }

  getClientUserList(clientId: number | string, filterValues?: FilterValues): Observable<UserInList[]> {
    const fields: (keyof User)[] = ['id', 'name', 'surname', 'userGroups', 'team']

    return this.getClientUsers(clientId, 0, 1000, null, filterValues, fields).pipe(
      map((response) => response['data'].map((user) => new User(user))),
    ) as unknown as Observable<UserInList[]>
  }

  exportFile(columns: string[], sort: string, filterValues: FilterValues, type: ExportFileTypes): Observable<string> {
    return this.apiService.exportFile(`/clients/${type}`, columns, sort, filterValues)
  }

  updateClientUser(
    clientId: number | string,
    userId: number | string,
    data: Partial<User> | FormData,
    fieldsSkipValueChecking = null,
  ): Observable<User> {
    const formatedSaveData = toFormData(data, null, null, fieldsSkipValueChecking)

    return this.apiService
      .post(`/clients/${clientId}/users/${userId}`, formatedSaveData, { context: withFeedback() })
      .pipe(map((response) => new User(response['data'])))
  }

  getAllClientUsers(): Observable<UserInList[]> {
    const params = {
      page: 0,
      limit: 1000,
      'fields[]': ['id', 'name', 'surname', 'team'],
    }
    return this.apiService
      .get(`/client/users`, { params })
      .pipe(map((response) => response['data'].map((user) => new User(user)))) as unknown as Observable<UserInList[]>
  }

  delete(userId: number | string, clientId: number) {
    return this.apiService.delete(`/clients/${clientId}/users/${userId}`, { context: withFeedback() })
  }

  undeleteUser(userId: number | string) {
    return this.apiService.post(`/users/${userId}/undelete`, {}, { context: withFeedback() })
  }

  restore(userId: number | string): Observable<User> {
    return this.apiService
      .post(`/users/${userId}/restore`, {}, { context: withFeedback() })
      .pipe(map((response) => new User(response['data'])))
  }

  archive(userId: number | string): Observable<User> {
    return this.apiService
      .patch(`/users/${userId}/archive`, {}, { context: withFeedback() })
      .pipe(map((response) => new User(response['data'])))
  }

  checkDriverId(teamId: number | string, userId: number | string, driverId: number | string): Observable<boolean> {
    const params = {
      teamId,
      userId,
      driverId,
    }

    return this.apiService.get('/check-driver-id', { params }).pipe(
      map((response) => response['data']),
      map((isUniqueDriverId: IsUniqueDriverId) => isUniqueDriverId.isUnique),
    )
  }

  getPlatformSettings(): Observable<PlatformSettings> {
    return this.apiService.get(`/client/platform-settings`).pipe(map((resp) => resp['data']))
  }

  getFilterUsers(
    url: string,
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof User)[],
  ): Observable<any> {
    const toUser = (user: User) => new User(user)

    // TODO: switch when Go supports paginated list
    return this.apiService
      .getPageable<Partial<User>>(url, pageIndex, pageSize, sort, filterValues, fields)
      .pipe(map(({ data, ...rest }) => ({ data: data.map(toUser), ...rest })))
  }

  getFilterTeams(
    url: string,
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof User)[],
  ): Observable<any> {
    const toUser = (user: User) => new User(user)

    // TODO: switch when Go supports paginated list
    return this.apiService
      .getPageable<Partial<User>>(url, pageIndex, pageSize, sort, filterValues, fields)
      .pipe(map(({ data, ...rest }) => ({ ...rest, data: data.map(toUser) })))
  }

  getDrivers(
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof Driver)[],
  ): Observable<PagesData<Driver>> {
    const toDriver = (driver: Driver) => new Driver(driver)

    // TODO: switch when Go supports paginated list
    return this.apiService
      .getPageable<Partial<Driver>>('/drivers', pageIndex, pageSize, sort, filterValues, fields)
      .pipe(map(({ data, ...rest }) => ({ data: data.map(toDriver), ...rest })))
  }

  getXeroContacts(params?: XeroGetContactsParams, hideId?: string): Observable<XeroContact[]> {
    // TODO(xero-fake): drop the fakeApis branch once BE implements GET /xero/contacts.
    const source$ = (
      environment.fakeApis
        ? of({ data: FALLBACK_XERO_CONTACTS as unknown as XeroContact[] })
        : this.apiService.get(`/xero/contacts`, { params })
    ) as Observable<{ data: XeroContact[] }>
    return source$.pipe(
      map((response) => response['data']),
      map((contacts: XeroContact[]) =>
        hideId ? contacts.map((contact) => ({ ...contact, hidden: contact.contactId === hideId })) : contacts,
      ),
      map((contacts: XeroContact[]) => contacts.map((contact) => ({ ...contact, disabled: !!contact.client }))),
    )
  }

  getClientXeroContact(clientId: string | number): Observable<XeroClientContact> {
    // TODO(xero-fake): remove fake branch once BE implements GET /xero/client/:id/contact.
    if (environment.fakeApis) return of(FALLBACK_XERO_CLIENT_CONTACT as unknown as XeroClientContact)
    return this.apiService.get(`/xero/client/${clientId}/contact`)
  }

  setClientXeroContact(ContactID: string, clientId: number): Observable<void> {
    // TODO(xero-fake): remove fake branch once BE implements POST /xero/client/contact.
    if (environment.fakeApis) return of(undefined)
    // NOTE: no withFeedback — chained after addClient/updateClient in client-form save (saveClient ->
    // saveXeroContact); those own the "client saved" toast, so a toast here would duplicate it.
    return this.apiService.post('/xero/client/contact', { ContactID, clientId })
  }
}
